import PhoneNumberField from "../PhoneNumberField";
import TextField from "../TextInput";
import { tw } from "@careinfo/tailwind";
import { useForm } from "react-hook-form";
import APIS from "../../../constant/EndPoint";
import { postApiFormData } from "../../../helpers/AxiosInstance";
import Button from "../Button";
import LocalCombobox from "../LocalCombobox";
import MultiImageField from "../MultiImageField";
import SignaturePad from "../SignaturePad";
import { toast } from "react-toastify";
import { useState } from "react";
import { yupResolver } from "@hookform/resolvers/yup/dist/yup";
import { deliveryformValidation } from "../../../utils/validationSchemas";
import { useSearchParams } from "react-router-dom";
import useGetHook from "../../../customHooks/useGetHook";
import { useQueryClient } from "@tanstack/react-query";

const DeliveryForm = (props: any) => {
  const { setIsOpen, deliveredStatus } = props;
  const [isLoading, setIsLoading] = useState(false);
  const IdType = [
    { name: "Driving Licence", id: "driving license" },
    { name: "National Identity", id: "national identity card" },
    { name: "Citizenship", id: "citizenship" },
    { name: "Passport", id: "passport" },
  ];

  const {
    control,
    register,
    setValue,
    clearErrors,
    handleSubmit,
    getValues,
    setError,
    formState: { errors },
  } = useForm<any>({
    resolver: yupResolver(deliveryformValidation),
  });

  const queryClient = useQueryClient();
  const [searchParams, setSearchParms] = useSearchParams();
  const selectedOrder = searchParams.get("selectedItems")?.split(",");
  const selectedTrackingNumber = searchParams.get("selectedWayBillNumber");

  const { data, isLoading: isDataLoading } = useGetHook({
    url: selectedTrackingNumber
      ? APIS.ORDER
      : `${APIS.ORDER}${selectedOrder?.[0]}/`,
    queryKey: [`order-${selectedOrder?.[0]}-${selectedTrackingNumber}`],
    params: {
      ...(selectedTrackingNumber
        ? {
            search: selectedTrackingNumber,
          }
        : null),
    },
  });

  const handleDeliver = async (formValues: any, setError: any) => {
    setIsLoading(true);
    const deliveredUrl = APIS.DELIVERED;
    const receiverUrl = APIS.RECEIVER_DETAIL;
    const deliveredFormData = new FormData();
    deliveredFormData.append("full_name", formValues.full_name);
    deliveredFormData.append("primary_contact", formValues.primary_contact);
    deliveredFormData.append("identity", formValues.identity);
    deliveredFormData.append("identity_number", formValues.identity_number);
    deliveredFormData.append(
      "order",
      selectedTrackingNumber ?? data?.tracking_number
    );
    deliveredFormData.append("remarks", formValues.remarks || "");

    if (formValues.identity_image) {
      deliveredFormData.append("identity_image", formValues.identity_image);
    }

    if (formValues.signature) {
      deliveredFormData.append("signature", formValues.signature);
    }
    const receiverFormData = new FormData();
    receiverFormData.append(
      "tracking_number",
      selectedTrackingNumber ?? data?.tracking_number
    );

    try {
      const deliveredRes = await postApiFormData({
        url: deliveredUrl,
        formData: receiverFormData,
        setError,
      });

      const receiverRes = await postApiFormData({
        url: receiverUrl,
        formData: deliveredFormData,
        setError,
      });
      if (deliveredRes?.status === 200 && receiverRes?.status === 201) {
        toast.success("Delivery submitted successfully!");
        searchParams.delete("selectedWayBillNumber");
        setSearchParms(searchParams);
        queryClient.invalidateQueries({
          queryKey: ["orders"],
          refetchType: "active",
        });
        setIsOpen(false);
      } else {
        toast.error("Something went wrong while submitting the delivery.");
      }
    } catch (err) {
      console.error("Delivery submission failed:", err);
      toast.error("Something went wrong while submitting the delivery.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDirectDeliver = async () => {
    const receiverFormData = new FormData();
    receiverFormData.append(
      "tracking_number",
      selectedTrackingNumber ?? data?.tracking_number
    );
    const deliveredUrl = APIS.DELIVERED;

    try {
      const deliveredRes = await postApiFormData({
        url: deliveredUrl,
        formData: receiverFormData,
        setError,
      });
      if (deliveredRes?.status === 200) {
        toast.success("Delivered successfully!");
        searchParams.delete("selectedWayBillNumber");
        setSearchParms(searchParams);
        queryClient.invalidateQueries({
          queryKey: ["orders"],
          refetchType: "active",
        });
        setIsOpen(false);
      } else {
        toast.error("Something went wrong while submitting the delivery.");
      }
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit((data) => handleDeliver(data, setError))}>
        <div className={tw`flex flex-col items-center gap-y-2`}>
          <div className={tw`w-full grid grid-cols-2 gap-2`}>
            <TextField
              label={"Full Name"}
              register={register("full_name")}
              errors={errors?.full_name}
            />
            <PhoneNumberField
              control={control}
              name={"primary_contact"}
              errors={errors?.primary_contact}
              label={"Mobile number"}
            />
            <LocalCombobox
              label={"Identity Type"}
              data={IdType}
              errors={errors?.identity}
              setValue={setValue}
              name={"identity"}
              register={register("identity")}
              clearErrors={clearErrors}
              value={getValues("identity")}
            />
            <TextField
              label={"Identity Number"}
              register={register("identity_number")}
              errors={errors?.identity_number}
            />
          </div>
          <MultiImageField name="identity_image" control={control} />
          <div className={tw`w-full `}>
            <label>
              <span>Remarks</span>
              <textarea
                rows={4}
                {...register("remarks")}
                className={tw`w-full focus:outline-none bg-[#EEF5F4] p-2 mt-1`}
              />
            </label>
            {errors?.remarks && (
              <p className={tw`text-red-500 text-xs my-1`}>
                {errors?.remarks?.message}
              </p>
            )}
          </div>
          <SignaturePad
            setValue={setValue}
            getValues={getValues}
            clearErrors={clearErrors}
            register={register}
            errors={errors?.signature}
            name="signature"
          />
        </div>
        <div>
          <Button
            label={"Submit"}
            isLoading={isLoading}
            disabled={
              selectedTrackingNumber
                ? data?.[0]?.delivery_status.name === deliveredStatus?.label
                : data?.delivery_status?.id === deliveredStatus?.id
            }
          />
          <Button
            label={"Skip & Proceed"}
            isLoading={isLoading}
            type={"button"}
            onClick={handleDirectDeliver}
          />
        </div>
      </form>
    </>
  );
};

export default DeliveryForm;
